import { action } from 'mobx';
import { observer } from 'mobx-react';
import * as React from 'react';

import { I18nContent } from '../../../adl-gen/totalinteraction/futurefit/db';
import { withI18n, WithI18nProps } from '../../../internationalization/i18n-hoc';
import { PlotKeyToggleState } from '../../../stores/plot-key-toggle-state';
import { Plot, QuizResultsHelper } from '../../../stores/quiz-results-store';
import { SelectOption, SelectOptionProps } from '../../widgets/select-option/select-option';
import { LabelProps, LineProps, SVGBuilder } from '../svg-builder/svg-builder';

const styles = require('./competency-radar.css');

export interface CompetencyProps {
  /// String key for lookup of scores
  key: string;

  /// Text of competency label (entry per newline row)
  labelTextRows: string[];

  /// Adjustment (svg coordinates) of label placement relative to their polar ellipse nominal placement
  labelAdjustment: { dx: number; dy: number };
}

export interface CompetencyLabelsPlacementProps {
  /// Radius (svg coordinates) of circular placement of score percentages
  actualScoresRadius: number;

  /// Two radii (svg coordinates) of the centers of texts of the ellipse to place the competency labels on.
  competencyLabels: {
    ellipseSemiAxisX: number;
    ellipseSemiAxisY: number;
  };
}

/** Props for the component */
export interface CompetencyRadarProps extends WithI18nProps {
  qr: QuizResultsHelper;

  labelsPlacement?: CompetencyLabelsPlacementProps;

  /// The list of radius values at which the grid is drawn.
  gridRadii?: number[];

  /// Size of the SVG viewbox (coordinates are centered around 0,0)
  viewBox?: {
    width: number;
    height: number;
  };

  /// State for toggles of result sets
  plotKeyToggleState: PlotKeyToggleState;
  selectedCompetency?: string;
  onCompetencyClick(key: string): void;
}

const toggleColorMap: { [key: string]: SelectOptionProps['color'] } = {
  actual: 'blue',
  expected: 'green',
  previous: 'violet',
  group: 'orange',
  industry: 'purple'
};

const layoutDebug = false;

@observer
export class CompetencyRadar0 extends React.Component<CompetencyRadarProps> {
  /** Renders the component */
  render() {
    const { qr, t } = this.props;

    // TODO: See generic function extractCompetenciesLayout @ end
    const competenciesLayout = qr.getCompetencies(t).map(c => {
      const multipleSpaces = /[ ]+/g;
      const nameTrimmed = t(c.name).trim();
      const labelTextRows = nameTrimmed
        .replace(multipleSpaces, ' ')
        .replace('& ', '--&---')
        .split(' ')
        .map(row => row.replace('--&---', '& '));

      const numberRows = labelTextRows.length;
      const maxLengthRow = labelTextRows.reduce((maxValue, currentItem) => Math.max(maxValue, currentItem.length), 0);

      return {
        labelTextRows,
        maxLengthRow,
        numberRows,
        name: c.name,
        labelAdjustment: { dx: 0, dy: 0 }
      };
    });

    const labelsPlacement = this.props.labelsPlacement || {
      actualScoresRadius: 120,
      competencyLabels: {
        ellipseSemiAxisX: 130,
        ellipseSemiAxisY: 130
      }
    };

    /// Use 'slice' because otherwise the plotkey toggle also toggles this off
    /// (f_k knows why)
    const gridRadii = this.props.gridRadii ? this.props.gridRadii.slice() : [20, 40, 60, 80, 100]; // ||
    // const gridRadii = [20, 40, 60, 80, 100];

    const viewBox = this.props.viewBox || {
      width: 400,
      height: 300
    };

    // the competencies angles:
    // TODO: See generic extractCompetenciesAngles @ end
    const competenciesAngles = qr.getCompetencies(t).map((_, i) => (i * 2.0 * Math.PI) / qr.getCompetencies().length);

    // TODO: See generic atRadiusAngleFn @ end
    const atRadiusAngle = function(value: number, i: number) {
      return {
        x: value * Math.sin(competenciesAngles[i]),
        y: -value * Math.cos(competenciesAngles[i])
      };
    };

    // format points for SVG polygon (text x,y x,y)
    const coordsLabelValue = competenciesAngles.map((_a, i) => atRadiusAngle(labelsPlacement.actualScoresRadius, i));

    const builder = new SVGBuilder(1, {
      posX: (-1 * viewBox.width) / 2,
      posY: (-1 * viewBox.height) / 2,
      dimX: viewBox.width,
      dimY: viewBox.height
    });

    // Circular grid
    for (const r of gridRadii.reverse()) {
      builder.addCircle({ x: 0, y: 0, r, style: 'background' });
    }
    // Radial grid
    for (let i = 0; i < competenciesAngles.length; i += 1) {
      const pointAtAngle = atRadiusAngle(100, i);
      builder.addLine({ x1: 0, y1: 0, x2: pointAtAngle.x, y2: pointAtAngle.y, style: 'solid', stroke: 'light' });
    }

    // Labels
    for (let i = 0; i < coordsLabelValue.length; i++) {
      const competencyKey = qr.getCompetencyKeys(t)[i];
      const x = atRadiusAngle(100, i).x;
      const y = atRadiusAngle(100, i).y;
      const style = coordinateToTextStyle(x, y);
      const coord = coordsLabelValue[i];
      const percentage = qr.scoreRatioOfExpectedText({ kind: 'competency', competencyId: competencyKey });
      const competency = competenciesLayout[i].labelTextRows.join(' ');
      builder.addLabel({
        x: coord.x,
        y: coord.y,
        anchor: style,
        text: percentage,
        subtext: competency.toUpperCase(),
        style: this.props.selectedCompetency === competencyKey ? 'highlighted' : 'regular',
        onClick: () => this.props.onCompetencyClick(competencyKey)
      });

      if (layoutDebug) {
        builder.addCircle({ x: coord.x, y: coord.y, style: 'filled', r: 5 });
      }
    }

    for (const plotkey of qr.getPlotKeys().filter(p => this.props.plotKeyToggleState.isShown(p))) {
      if (!qr.plotExists(plotkey)) {
        continue;
      }
      const scores = qr
        .getCompetencyKeys(t)
        .map(ckey => qr.score(plotkey, { kind: 'competency', competencyId: ckey }))
        .map(atRadiusAngle);
      // Lines
      for (let i = 0; i < scores.length - 1; i++) {
        const score = scores[i];
        const nextScore = scores[i + 1];
        builder.addLine({
          x1: score.x,
          y1: score.y,
          x2: nextScore.x,
          y2: nextScore.y,
          style: plotKeyToLineStyle(plotkey),
          stroke: plotkey === 'previous'
            ? 'dark' : 'medium'
        });
      }
      builder.addLine({
        x1: scores[0].x,
        y1: scores[0].y,
        x2: scores[scores.length - 1].x,
        y2: scores[scores.length - 1].y,
        style: plotKeyToLineStyle(plotkey),
        stroke: plotkey === 'previous'
          ? 'dark' : 'medium'
      });

      for (const score of scores) {
        if (plotkey === 'expected') {
          builder.addCircle({ x: score.x, y: score.y, r: 2, style: 'filled' });
        } else if (plotkey === 'group') {
          builder.addCircle({ x: score.x, y: score.y, r: 2, style: 'shaded' });
        } else if (plotkey === 'industry') {
          builder.addTriangle({ x: score.x, y: score.y, size: 2 });
        }
      }
      // }
    }

    return builder.build();
  }
}

export const CompetencyRadar = withI18n(CompetencyRadar0);

export function coordinateToTextStyle(x: number, y: number): LabelProps['anchor'] {
  const offsetToCenter = 10;
  let suffix: string = 'middle';
  if (x > offsetToCenter) {
    suffix = 'right';
  } else if (x < -offsetToCenter) {
    suffix = 'left';
  }
  let prefix: string = 'middle';
  if (y > offsetToCenter) {
    prefix = 'bottom';
  } else if (y < -offsetToCenter) {
    prefix = 'top';
  }
  if (prefix === 'middle' && suffix === 'middle') {
    return 'center';
  } else if (prefix === 'middle') {
    return suffix as LabelProps['anchor'];
  } else if (suffix === 'middle') {
    return prefix as LabelProps['anchor'];
  } else {
    const style = prefix + suffix;
    return style as LabelProps['anchor'];
  }
}

export function plotKeyToLineStyle(plotKey: keyof Plot): LineProps['style'] {
  if (plotKey === 'actual') {
    return 'primary';
  } else if (plotKey === 'expected') {
    return 'solid';
  } else if (plotKey === 'previous' || plotKey === 'industry'
  ) {
    return 'dashed';
  } else if (plotKey === 'group') {
    return 'dotted';
  }
  throw Error(`Unexpected plotkey ${plotKey} for linestyle`);
}

interface PlotKeyLegendLabelProps {
  plotkey: string;
  title: string;
  disabled?: boolean;

  plotKeyToggleState: PlotKeyToggleState;
}

/**
 * Renders the legend (also toggle button action)
 */
@observer
export class PlotKeyLegendLabel extends React.Component<PlotKeyLegendLabelProps> {
  /** Renders the component */
  render() {
    return (
      <SelectOption
        className={styles.toggle}
        onChange={async () => this.onClick()}
        label={this.props.title}
        selected={this.props.disabled ? false : this.props.plotKeyToggleState.isShown(this.props.plotkey)}
        color={toggleColorMap[this.props.plotkey]}
        round={true}
        disabled={this.props.disabled}
      />
    );
  }

  @action onClick() {
    this.props.plotKeyToggleState.toggle(this.props.plotkey);
  }
}

export interface CompetenciesLayoutItem {
  labelTextRows: string[];
  maxLengthRow: number;
  numberRows: number;
  name: I18nContent;
  labelAdjustment: {
    dx: number;
    dy: number;
  };
}

// export function extractCompetenciesLayout(helper: QuizResultsHelper, t: TranslateFn): CompetenciesLayoutItem[] {
//   return helper.getCompetencies().map(c => {
//     const multipleSpaces = /[ ]+/g;
//     const nameTrimmed = t(c.name).trim();
//     const labelTextRows = nameTrimmed
//       .replace(multipleSpaces, " ")
//       .replace("& ", "--&---")
//       .split(" ")
//       .map(row => row.replace("--&---", "& "));

//     const numberRows = labelTextRows.length;
//     const maxLengthRow = labelTextRows.reduce(
//       (maxValue, currentItem) => Math.max(maxValue, currentItem.length),
//       0
//     );

//     return {
//       labelTextRows,
//       maxLengthRow,
//       numberRows,
//       name: c.name,
//       labelAdjustment: { dx: 0, dy: 0 }
//     };
//   });
// }

// export function extractCompetenciesAngles(helper: QuizResultsHelper): number[] {
//   return helper.getCompetencies().map(
//     (_, i) => (i * 2.0 * Math.PI) / helper.getCompetencies().length
//   );
// }

// export const atRadiusAngleFn = (competenciesAngles: number[]) => (value: number, i: number) => {
//   return {
//     x: value * Math.sin(competenciesAngles[i]),
//     y: -value * Math.cos(competenciesAngles[i])
//   };
// };



// WEBPACK FOOTER //
// ./src/ui/components/competency-radar/competency-radar.tsx